import { 
  Eye, EyeOff, Trash2, ChevronUp, GripVertical, Layout, Edit2
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * SortableSectionItem - Draggable section item for homepage section manager
 * Supports visibility toggle, edit, delete, and drag-to-reorder
 */
const SortableSectionItem = ({ 
  section, 
  onToggle, 
  onEdit, 
  onDelete, 
  onRestore, 
  isExpanded, 
  onToggleExpand, 
  children 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  const Icon = section.icon || Layout;
  const isBuiltin = section.builtin;
  const isCustom = !isBuiltin;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg overflow-hidden ${
        isDragging ? 'shadow-xl shadow-orange-500/20 ring-2 ring-orange-500' : ''
      } ${section.enabled ? 'bg-zinc-900 border-zinc-700' : 'bg-zinc-900/50 border-zinc-800'}`}
    >
      {/* Section Header */}
      <div className="flex items-center gap-3 p-4">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-zinc-500 hover:text-orange-500 transition-colors touch-none"
        >
          <GripVertical className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${
          section.enabled ? 'bg-orange-500/10 text-orange-500' : 'bg-zinc-800 text-zinc-600'
        }`}>
          <Icon className="w-5 h-5" />
        </div>

        {/* Name & Type */}
        <div className="flex-1 min-w-0">
          <div className={`font-bold truncate ${section.enabled ? 'text-white' : 'text-zinc-500'}`}>
            {section.name}
          </div>
          <div className="text-xs text-zinc-500">
            {isBuiltin ? 'Built-in Section' : `Custom: ${section.template_name || section.template_id}`}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Toggle Visibility */}
          <button
            onClick={() => onToggle(section.id)}
            className={`p-2 rounded transition-colors ${
              section.enabled 
                ? 'text-green-500 hover:bg-green-500/10' 
                : 'text-zinc-600 hover:bg-zinc-800'
            }`}
            title={section.enabled ? "Hide section" : "Show section"}
          >
            {section.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>

          {/* Edit (custom sections only) */}
          {isCustom && (
            <button
              onClick={() => onToggleExpand(section.id)}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
              title="Edit content"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
            </button>
          )}
          
          {/* Edit (hero built-in section) */}
          {isBuiltin && section.id === 'hero' && (
            <button
              onClick={() => onToggleExpand(section.id)}
              className="p-2 text-orange-400 hover:text-orange-300 hover:bg-zinc-800 rounded transition-colors"
              title="Edit hero section"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
            </button>
          )}
          
          {/* Edit (videos built-in section) */}
          {isBuiltin && section.id === 'videos' && (
            <button
              onClick={() => onToggleExpand(section.id)}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
              title="Edit videos"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
            </button>
          )}
          
          {/* Edit (sponsors built-in section) */}
          {isBuiltin && section.id === 'sponsors' && (
            <button
              onClick={() => onToggleExpand(section.id)}
              className="p-2 text-orange-400 hover:text-orange-300 hover:bg-zinc-800 rounded transition-colors"
              title="Edit sponsors"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
            </button>
          )}

          {/* Delete (ALL sections can be deleted) */}
          <button
            onClick={() => onDelete(section.id, isBuiltin)}
            className="p-2 text-zinc-400 hover:text-red-500 hover:bg-zinc-800 rounded transition-colors"
            title="Delete section"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expandable Content Editor (for sections with content) */}
      {isExpanded && children && (
        <div className="border-t border-zinc-800 p-4 bg-zinc-950">
          {children}
        </div>
      )}
    </div>
  );
};

export default SortableSectionItem;
